import { RefinementCtx, z } from "zod";
import type { AgreementTypeEnum, CalculationMethodType, AgreementConditionType } from "__generated__/graphql";
import { AgreementSchema } from "view/agreements/schema";
import { CalculationMethodValidationSchema } from "view/calculationmethods/validation/calculationMethod.zod";
import { AgreementValidationSchema } from "view/agreements/validation/agreement.zod";
import { AgreementConditionValidationSchema } from "view/agreementconditions/validation/agreementcondition.zod";
import { AgreementConditionSchema } from "view/agreementconditions/schema";
import { throwError } from "@validation/utils/schemaUtils";
import { TierValidationSchema } from "view/calculationmethods/validation/tier.zod";
import { CalculationMethodSchema } from "view/calculationmethods/schema";

export type AgreementRow = z.infer<typeof AgreementValidationSchema>;
export type CalculationMethodRow = z.infer<typeof CalculationMethodValidationSchema>;
export type AgreementConditionRow = z.infer<typeof AgreementConditionValidationSchema>;
export type TierRow = z.infer<typeof TierValidationSchema>;

export type RowValidationContext = {
	agreementType?: AgreementTypeEnum;
	calculationMethodType: CalculationMethodType | null;
	conditionType?: AgreementConditionType;
	agreement?: AgreementRow;
	calculationMethod?: CalculationMethodRow;
	condition?: AgreementConditionRow;
}

export const getRequiredAgreementFields = (context: RowValidationContext) => {
	const schema = context.agreementType ? AgreementSchema[context.agreementType] : undefined;

	const fields = {
		...(schema?.agreementFields ?? {}),
		...(schema?.calculationMethodFields ?? {}),
	};

	return Object.values(fields).filter((x) => (
		(!x.rules?.hidden || !x.rules?.hiddenIf?.(context))
		&& (x.rules?.required
			|| x.rules?.requiredIf?.(context))
	));
};

export const getRequiredCalculationMethodFields = (context: RowValidationContext) => {
	const schema = context.calculationMethodType ? CalculationMethodSchema[context.calculationMethodType] : undefined;

	if (schema?.fields == null) {
		return;
	}

	return Object.values(schema.fields).filter((x) => (
		(!x.rules?.hidden || !x.rules?.hiddenIf?.(context))
		&& (x.rules?.required
			|| x.rules?.requiredIf?.(context))
	));
};

export const getRequiredConditionFields = (context: RowValidationContext) => {
	const schema = context.conditionType ? AgreementConditionSchema[context.conditionType] : undefined;

	if (schema?.fields == null) {
		return;
	}

	return Object.values(schema.fields).filter((x) => (
		(!x.rules?.hidden || !x.rules?.hiddenIf?.(context))
		&& (x.rules?.required
			|| x.rules?.requiredIf?.(context))
	));
};

export const validateTiers = (tiers: TierRow[], key: keyof TierRow, ctx: RefinementCtx) => {
	tiers.forEach((tier, i) => {
		const value = tier[key];

		if (value == null) {
			throwError([ "tiers", i, key ], ctx);
		}
	});
};
